import { Component } from '@theme/component';

/**
 * Scent finder slider: every scent panel is one slide of a horizontal scroll-snap track.
 * Swiping checks the matching tab radio and scrolls the tab row to it, choosing a tab
 * scrolls the track to its slide, and the track height follows the current slide.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track - The scroll-snap track holding the scent panels
 * @property {HTMLElement} tabs - The tab row with the scent radios
 * @property {HTMLElement[]} [dots] - Position dots, one per scent
 *
 * @extends {Component<Refs>}
 */
class ScentFinderComponent extends Component {
  requiredRefs = ['track', 'tabs'];

  #index = 0;

  /** @type {number | null} Slide the track is scrolling to after a tab or arrow choice */
  #targetIndex = null;

  #frame = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #scrollEndTimer;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.track.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#updateHeight());
    for (const slide of this.slides) this.#resizeObserver.observe(slide);

    this.#index = Math.max(
      0,
      this.inputs.findIndex((input) => input.checked)
    );
    this.select(this.#index, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#scrollEndTimer);
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.track.children).filter((element) => element.classList.contains('scent-finder__panel'))
    );
  }

  /** @returns {HTMLInputElement[]} */
  get inputs() {
    return Array.from(this.refs.tabs.querySelectorAll('input[data-scent-tab-input]'));
  }

  /**
   * Shows a scent: scrolls its slide into the track and selects its tab.
   *
   * @param {number} index - Slide index; wraps around at both ends
   * @param {{ animate?: boolean }} [options]
   */
  select(index, { animate = true } = {}) {
    const count = this.slides.length;
    if (count === 0) return;

    this.#index = ((index % count) + count) % count;
    const smooth = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#targetIndex = smooth ? this.#index : null;
    this.#scrollTrackTo(this.#index, smooth);
    this.#syncControls(smooth);
    this.#updateHeight();
  }

  /** Handles the previous arrow. */
  selectPrevious() {
    this.select(this.#index - 1);
  }

  /** Handles the next arrow. */
  selectNext() {
    this.select(this.#index + 1);
  }

  /**
   * @param {number} index
   * @param {boolean} smooth
   */
  #scrollTrackTo(index, smooth) {
    const { track } = this.refs;
    const slide = this.slides[index];
    if (!slide) return;

    // Rect difference works the same in LTR and RTL scroll containers
    const delta = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (Math.abs(delta) < 1) {
      this.#targetIndex = null;
      return;
    }

    track.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
  }

  /**
   * Checks the current tab, scrolls the tab row horizontally to it and moves the dots.
   *
   * @param {boolean} smooth
   */
  #syncControls(smooth) {
    const input = this.inputs[this.#index];
    if (input) {
      input.checked = true;

      const tab = input.closest('.scent-finder__tab');
      const { tabs } = this.refs;
      if (tab && tabs.scrollWidth > tabs.clientWidth) {
        const tabRect = tab.getBoundingClientRect();
        const tabsRect = tabs.getBoundingClientRect();
        const delta = tabRect.left + tabRect.width / 2 - (tabsRect.left + tabsRect.width / 2);
        // Only the tab row scrolls, so the page never moves vertically
        tabs.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' });
      }
    }

    for (const [dotIndex, dot] of (this.refs.dots ?? []).entries()) {
      dot.toggleAttribute('data-active', dotIndex === this.#index);
    }
  }

  #updateHeight() {
    const slide = this.slides[this.#index];
    if (!slide) return;

    this.refs.track.style.height = `${slide.offsetHeight}px`;
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const { track } = this.refs;
      if (track.clientWidth === 0) return;

      const current = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);

      if (this.#targetIndex !== null) {
        // Ignore the slides a tab or arrow choice scrolls past
        if (current === this.#targetIndex) this.#targetIndex = null;
      } else if (current !== this.#index && this.slides[current]) {
        this.#index = current;
        this.#syncControls(true);
        this.#updateHeight();
      }
    });

    // Fallback for interrupted smooth scrolls: stop waiting once scrolling settles
    clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = setTimeout(() => {
      this.#targetIndex = null;
    }, 200);
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-scent-tab-input')) return;

    const index = this.inputs.indexOf(target);
    if (index !== -1) this.select(index);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const slide = target.closest('.scent-finder__panel');
    const index = this.slides.findIndex((element) => element === slide);
    if (index !== -1) this.select(index, { animate: false });
  };
}

if (!customElements.get('scent-finder-component')) {
  customElements.define('scent-finder-component', ScentFinderComponent);
}
